import { definePreset } from '@openng/optimus-ui-themes';
import nora from '@openng/optimus-ui-themes/nora';

export const navFleetSurfacePalettes = {
    navFleetSlateMist: {
        [0]: '#ffffff',
        [50]: '#e6e7e8',
        [100]: '#cdcfd1',
        [200]: '#b4b6ba',
        [300]: '#9b9ea3',
        [400]: '#82868c',
        [500]: '#686e75',
        [600]: '#4f565e',
        [700]: '#363d47',
        [800]: '#1d2530',
        [850]: '#0f1c24',
        [900]: '#08111b',
        [950]: '#040d19',
    },
};

export const navFleetColorPalettes = {
    primitive: {
        ...navFleetSurfacePalettes,
        navFleetDeepOcean: {
            [50]: '#f3f5f7',
            [100]: '#c6cfda',
            [200]: '#9aa9bd',
            [300]: '#6d84a0',
            [400]: '#405e82',
            [450]: '#3ba8c9',
            [500]: '#133865',
            [600]: '#103056',
            [700]: '#152b3c',
            [800]: '#0a1f38',
            [850]: '#0f1c24',
            [900]: '#081628',
            [950]: '#050e19',
        },
    },
};

export const themePreset = definePreset(nora, {
    ...navFleetColorPalettes,
    semantic: {
        primary: {
            [50]: '{navFleetDeepOcean.50}',
            [100]: '{navFleetDeepOcean.100}',
            [200]: '{navFleetDeepOcean.200}',
            [300]: '{navFleetDeepOcean.300}',
            [400]: '{navFleetDeepOcean.400}',
            [450]: '{navFleetDeepOcean.450}',
            [500]: '{navFleetDeepOcean.500}',
            [600]: '{navFleetDeepOcean.600}',
            [700]: '{navFleetDeepOcean.700}',
            [800]: '{navFleetDeepOcean.800}',
            [900]: '{navFleetDeepOcean.900}',
            [950]: '{navFleetDeepOcean.950}',
        },
        colorScheme: {
            dark: {
                surface: {
                    [0]: '{navFleetSlateMist.0}',
                    [50]: '{navFleetSlateMist.50}',
                    [100]: '{navFleetSlateMist.100}',
                    [200]: '{navFleetSlateMist.200}',
                    [300]: '{navFleetSlateMist.300}',
                    [400]: '{navFleetSlateMist.400}',
                    [500]: '{navFleetSlateMist.500}',
                    [600]: '{navFleetSlateMist.600}',
                    [700]: '{navFleetSlateMist.700}',
                    [800]: '{navFleetSlateMist.800}',
                    [850]: '{navFleetSlateMist.850}',
                    [900]: '{navFleetSlateMist.900}',
                    [950]: '{navFleetSlateMist.950}',
                },
                primary: {
                    color: '{primary.700}',
                    contrastColor: '{surface.0}',
                    hoverColor: '{primary.600}',
                    activeColor: '{primary.500}',
                },
                highlight: {
                    background: '{primary.700}',
                    focusBackground: '{primary.600}',
                    color: '{surface.0}',
                    focusColor: '{surface.0}',
                },
                formField: {
                    borderColor: '{surface.600}',
                    hoverBorderColor: '{primary.500}',
                    disabledBackground: '{surface.800}',
                    disabledColor: '{surface.500}',
                    paddingX: '0.5rem',
                },
                content: {
                    borderColor: '{surface.700}',
                },
            },
        },
    },
    components: {
        chip: {
            colorScheme: {
                dark: {
                    root: {
                        background: '{primary.950}',
                    },
                },
            },
        },
        button: {
            root: {
                fontSize: '0.875rem',
                gap: '0.4rem',
                iconOnlyWidth: '2rem',
                label: {
                    fontWeight: '400',
                },
                lg: {
                    fontSize: '1rem',
                    iconOnlyWidth: '2.5rem',
                    paddingX: '0.75rem',
                    paddingY: '0.5rem',
                },
                paddingX: '0.625rem',
                paddingY: '0.325rem',
                sm: {
                    fontSize: '0.75rem',
                    iconOnlyWidth: '1.5rem',
                    paddingX: '0.5rem',
                    paddingY: '0.2rem',
                },
            },
            colorScheme: {
                dark: {
                    root: {
                        success: {
                            background: '{primary.500}',
                            hoverBackground: '{primary.300}',
                            borderColor: '{primary.500}',
                            hoverBorderColor: '{primary.300}',
                            color: '{surface.0}',
                            hoverColor: '{surface.0}',
                        },
                    },
                    text: {
                        primary: {
                            color: '{surface.0}',
                            hoverBackground: '{primary.500}',
                        },
                    },
                    outlined: {
                        primary: {
                            color: '{surface.0}',
                            hoverBackground: '{primary.500}',
                        },
                    },
                },
            },
        },
        radiobutton: {
            root: {
                disabledBackground: '{surface.600}',
            },
            icon: {
                checkedColor: '{surface.0}',
                checkedHoverColor: '{surface.0}',
            },
        },
        tooltip: {
            root: {
                maxWidth: '300px',
            },
            colorScheme: {
                dark: {
                    root: {
                        background: '{surface.900}',
                        color: '{surface.0}',
                    },
                },
            },
        },
        toast: {
            colorScheme: {
                dark: {
                    warn: {
                        background: '{yellow.800}',
                        borderColor: '{yellow.600}',
                        color: '{yellow.600}',
                        detailColor: '{yellow.600}',
                    },
                },
            },
        },
        message: {
            text: {
                fontWeight: '500',
            },
            colorScheme: {
                dark: {
                    warn: {
                        background: '{yellow.800}',
                        borderColor: '{yellow.600}',
                        color: '{yellow.600}',
                    },
                    error: {
                        background: '{red.800}',
                        borderColor: '{red.600}',
                        color: '{red.600}',
                        simple: {
                            color: '{red.600}',
                        },
                    },
                },
            },
        },
        checkbox: {
            root: {
                checkedBackground: '{primary.500}',
            },
        },
        tabs: {
            tab: {
                padding: '0.5rem 0.5rem',
            },
        },
        listbox: {
            root: {
                background: '{surface.900}',
                borderColor: 'transparent',
            },
            option: {
                focusBackground: '{surface.700}',
            },
        },
        select: {
            dropdown: {
                width: '1.75rem',
            },
            option: {
                padding: '0.5rem 0.5rem',
            },
        },
        badge: {
            root: {
                borderRadius: '50%',
                padding: '0',
            },
            colorScheme: {
                dark: {
                    info: {
                        color: '{surface.0}',
                    },
                },
            },
        },
    },
} as typeof nora);
